import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardCheck, Building2, CalendarDays } from "lucide-react";
import { api, type AnalyticsOverview } from "../../lib/api";

const PLAN_LABELS: Record<string, string> = {
  free: "Free",
  solo: "Solo",
  studio: "Studio",
};

export default function Dashboard() {
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.analytics
      .overview()
      .then(setOverview)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const stats = [
    { label: "Posts generated", value: overview?.postsGenerated ?? "—" },
    {
      label: "Approval rate",
      value: overview ? `${Math.round(overview.approvalRate * 100)}%` : "—",
    },
    {
      label: "Pending review",
      value: overview
        ? overview.postsGenerated -
          overview.postsApproved -
          overview.postsRejected
        : "—",
    },
    { label: "Published", value: overview?.postsPublished ?? "—" },
  ];

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="text-sm text-muted mt-1">Your content at a glance.</p>
        </div>
        <Link
          to="/brands"
          className="btn-primary whitespace-nowrap text-xs sm:text-sm"
        >
          + New campaign
        </Link>
      </div>

      {/* Plan usage bar */}
      {overview?.cycleUsage && (
        <div className="card p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap justify-between text-sm mb-1 gap-x-3">
              <span className="font-medium text-slate">
                {PLAN_LABELS[overview.cycleUsage.plan]} plan
              </span>
              <span className="text-muted">
                {overview.cycleUsage.used === Infinity
                  ? "Unlimited"
                  : `${overview.cycleUsage.used} / ${overview.cycleUsage.limit} posts this cycle`}
              </span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-brand-500 rounded-full transition-all"
                style={{
                  width: `${Math.min((overview.cycleUsage.used / (overview.cycleUsage.limit || 1)) * 100, 100)}%`,
                }}
              />
            </div>
          </div>
          {overview.cycleUsage.plan === "free" && (
            <Link
              to="/billing"
              className="btn-secondary text-xs whitespace-nowrap self-start sm:self-auto"
            >
              Upgrade
            </Link>
          )}
        </div>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {stats.map(({ label, value }) => (
          <div key={label} className="card p-5">
            <p className="text-sm text-muted mb-1">{label}</p>
            <p className="text-3xl font-bold text-ink">
              {loading ? "—" : value}
            </p>
          </div>
        ))}
      </div>

      {/* Quick actions */}
      <h2 className="eyebrow mb-3">Quick actions</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link
          to="/review"
          className="card p-5 hover:border-brand-200 hover:shadow-md transition-all group"
        >
          <ClipboardCheck className="h-6 w-6 text-brand-500 mb-2" />
          <p className="font-semibold text-ink group-hover:text-brand-600">
            Review queue
          </p>
          <p className="text-sm text-muted mt-1">
            Approve or edit generated posts
          </p>
        </Link>
        <Link
          to="/brands"
          className="card p-5 hover:border-brand-200 hover:shadow-md transition-all group"
        >
          <Building2 className="h-6 w-6 text-brand-500 mb-2" />
          <p className="font-semibold text-ink group-hover:text-brand-600">
            Brands & campaigns
          </p>
          <p className="text-sm text-muted mt-1">
            Manage brand profiles and campaigns
          </p>
        </Link>
        <Link
          to="/calendar"
          className="card p-5 hover:border-brand-200 hover:shadow-md transition-all group"
        >
          <CalendarDays className="h-6 w-6 text-brand-500 mb-2" />
          <p className="font-semibold text-ink group-hover:text-brand-600">
            Content calendar
          </p>
          <p className="text-sm text-muted mt-1">Schedule approved posts</p>
        </Link>
      </div>
    </div>
  );
}
